/**
 * listBlocks — the config layer behind the generic list-input blocks.
 *
 * A "block" is a reusable row that clips above or below any domain's roster in
 * list view: Header (column labels), Out-of (the "/ N" row), Weighting (per-
 * column weights + a Total page), Average (class-average footer). Blocks are
 * blind to which domain they serve — they key off column SHAPE (field type),
 * never off a domain id.
 *
 * Defaults are contextual and overridable per domain via `domain.listBlocks`
 * ({ outOf, average, weighting } booleans), edited in Domain settings.
 * The native copy is generated from this source.
 *
 * Average semantics per column type (generic, not marks-shaped):
 *   number      → mean, shown as % when the column has an "out of"
 *   binary      → % Yes over answered rows
 *   checkbox    → % checked over the roster
 *   star        → mean stars ("3.4 / 5")
 *   multiChoice → most common answer + its share ("Present · 78%")
 *   text        → no average
 */

import { classAvg, columnPct } from '../../grades/utils/gradebookCompute';
import { getDomainNumericMaximum } from '../../../services/domain/recordingContract';

const AVERAGEABLE_TYPES = new Set(['number', 'boolean', 'binary', 'checkbox', 'star', 'multiChoice']);

export function isAverageableColumn(column) {
  return AVERAGEABLE_TYPES.has(column?.type);
}

/**
 * The "out of" for a column, or null when it has none (a count field like
 * "Laps run" averages as a plain mean, not a percentage). The synthetic
 * single-number column (__domainScore) takes the session override first —
 * that's the editable Out-of row — then the domain's configured max.
 */
export function resolveColumnMax(domain, column, sessionMaxScore = null) {
  if (column?.type !== 'number') return null;
  if (column.id === '__domainScore') {
    return getDomainNumericMaximum(domain, sessionMaxScore);
  }
  return column.maxScore ?? null;
}

/**
 * Which blocks are on for this domain + column set. `domain.listBlocks` wins
 * when a key is an explicit boolean; otherwise the contextual default applies:
 * header always, out-of and average when there's a number column. Ordinary
 * totals need explicit school-configured contributions; protected callers keep
 * their established presentation default.
 */
export function effectiveListBlocks(domain, columns) {
  const cols = Array.isArray(columns) ? columns : [];
  const numberCols = cols.filter((c) => c?.type === 'number');
  const overrides = domain?.listBlocks || {};
  const pick = (key, fallback) =>
    typeof overrides[key] === 'boolean' ? overrides[key] : fallback;
  return {
    header: true,
    outOf: pick('outOf', numberCols.length > 0),
    average: pick('average', numberCols.length > 0),
    weighting: pick('weighting', domain?.runtime?.protected === true
      ? numberCols.length >= 2 : numberCols.filter(column => column.weight > 0).length >= 2),
  };
}

/**
 * Latest recorded value per student for one column, from a newest-first event
 * list (first hit per student wins — same resolution web uses). Reads the
 * canonical top-level contract: real field ids sit at the doc top level
 * (legacy docs nested them under fieldValues), the synthetic single-number
 * column reads numericValue + maxScore, binary/text singles read typeLabel.
 *
 * @returns {{ [studentId]: { value: any, max: number|null } }}
 */
export function latestPriorByStudent(events, column, domain) {
  const out = {};
  if (!column) return out;
  const domainMax = getDomainNumericMaximum(domain);
  for (const ev of events || []) {
    const sid = ev?.studentId;
    if (!sid || sid in out) continue;
    let value = null;
    let max = null;
    if (column.id === '__domainScore') {
      value = typeof ev.numericValue === 'number'
        ? ev.numericValue
        : (typeof ev?.fieldValues?.score === 'number' ? ev.fieldValues.score : null);
      max = typeof ev.maxScore === 'number' && ev.maxScore > 0 ? ev.maxScore : domainMax;
    } else if (column.id === '__domainBinary') {
      value = typeof ev.typeLabel === 'boolean' || ev.typeLabel === 'absent' ? ev.typeLabel : null;
    } else if (column.id === '__domainText') {
      value = typeof ev.typeLabel === 'string' ? ev.typeLabel : null;
    } else if (column.id === '__domainType') {
      value = ev.typeId ?? null;
    } else {
      value = ev[column.id] ?? ev?.fieldValues?.[column.id] ?? null;
      if (column.type === 'number') {
        max = column.maxScore ?? null;
        if (typeof value !== 'number') value = null;
      }
    }
    if (value !== null && value !== undefined && value !== '') {
      out[sid] = { value, max };
    }
  }
  return out;
}

/**
 * The class-average text for one column over resolved cell entries
 * ({ value, max } per student — live entry first, latest recorded otherwise).
 * Returns null when the column type has no meaningful average.
 */
export function columnAverageText(column, entries, { rosterCount = 0 } = {}) {
  const list = (entries || []).filter((e) => e && e.value !== null && e.value !== undefined && e.value !== '');
  if (!column || !isAverageableColumn(column)) return null;

  if (column.type === 'number') {
    if (list.length === 0) return null;
    const withMax = list.filter((e) => typeof e.max === 'number' && e.max > 0);
    if (withMax.length === list.length) {
      const avg = classAvg(list.map((e) => columnPct(e.value, e.max)));
      return avg == null ? null : `${Math.round(avg)}%`;
    }
    const nums = list.map((e) => Number(e.value)).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return null;
    const mean = nums.reduce((s, n) => s + n, 0) / nums.length;
    return `${Math.round(mean * 10) / 10}`;
  }

  if (column.type === 'binary' || column.type === 'boolean') {
    const answered = list.filter((e) => e.value === true || e.value === false || e.value === 'absent');
    if (answered.length === 0) return null;
    const yes = answered.filter((e) => e.value === true).length;
    return `${Math.round((yes / answered.length) * 100)}% Yes`;
  }

  if (column.type === 'checkbox') {
    if (!rosterCount) return null;
    const checked = list.filter((e) => e.value === true).length;
    return `${Math.round((checked / rosterCount) * 100)}%`;
  }

  if (column.type === 'star') {
    const stars = list.map((e) => Number(e.value)).filter((n) => !Number.isNaN(n) && n > 0);
    if (stars.length === 0) return null;
    const mean = stars.reduce((s, n) => s + n, 0) / stars.length;
    return `${Math.round(mean * 10) / 10} / 5`;
  }

  if (column.type === 'multiChoice') {
    if (list.length === 0) return null;
    const counts = {};
    for (const e of list) {
      const k = String(e.value);
      counts[k] = (counts[k] || 0) + 1;
    }
    const [topValue, topCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    const options = (column.options || []).map((o) =>
      typeof o === 'string' ? { value: o, label: o } : { value: o.value ?? o.key, label: o.label ?? String(o.value ?? o.key) }
    );
    const label = options.find((o) => String(o.value) === topValue)?.label || topValue;
    return `${label} · ${Math.round((topCount / list.length) * 100)}%`;
  }

  return null;
}

export function listInputColumns(domain) {
  if (!domain) return [];

  const fields = domain.primary
    ? [domain.primary]
    : Array.isArray(domain.fields)
      ? [...domain.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : [];
  if (fields.length > 0) return fields;

  const fieldType = domain.fieldType
    || (domain.defaultInputMethod === 'number' ? 'number' : null);
  if (fieldType === 'number') {
    return [{ id: '__domainScore', label: 'Score', type: 'number' }];
  }
  if (fieldType === 'binary' || fieldType === 'boolean') {
    return [{ id: '__domainBinary', label: 'Yes / No', type: 'binary' }];
  }
  if (fieldType === 'text') {
    return [{ id: '__domainText', label: 'Note', type: 'text' }];
  }
  // Legacy type-based domains render their types as one choice column.
  return [{ id: '__domainType', label: 'Type', type: 'multiChoice' }];
}
